export type NoiseSource = 'none' | 'perlinNoise' | 'turbulence'

export type DitherPattern =
  | 'none'
  | 'checkerboard'
  | 'bayer4'
  | 'bayer8'
  | 'horizontalLines'
  | 'verticalLines'
  | 'diagonalLines'
  | 'dots'

// `turbulence` folds the noise around 0.5 for crunchier ridge edges.
export type BandBoundaryNoiseType = 'value' | 'turbulence'

// Shape of each halftone dot: classic round dots or hard-edged squares.
export type DotShape = 'circle' | 'square'

const NOISE_SOURCES: readonly NoiseSource[] = [
  'none',
  'perlinNoise',
  'turbulence',
] as const

const BAND_BOUNDARY_NOISE_TYPES: readonly BandBoundaryNoiseType[] = [
  'value',
  'turbulence',
] as const

const DITHER_PATTERNS: readonly DitherPattern[] = [
  'none',
  'checkerboard',
  'bayer4',
  'bayer8',
  'horizontalLines',
  'verticalLines',
  'diagonalLines',
  'dots',
] as const

const DOT_SHAPES: readonly DotShape[] = ['circle', 'square'] as const

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)

// One control point of a confidence → dither-density map (used by both the
// trailing-actor black field and the loss-of-control red field). Both values in
// [0,1]; linear interpolation between stops (see the ⌘+G editors).
export type ConfidenceDensityStop = {
  confidence: number
  density: number
}
// Back-compat alias.
export type TrailingActorRiskConfidenceStop = ConfidenceDensityStop

// Dither density for a given confidence, linearly interpolated between the
// (ascending-by-confidence) stops and clamped to the end stops' densities.
export const densityFromConfidenceStops = (
  confidence: number,
  stops: TrailingActorRiskConfidenceStop[],
): number => {
  if (stops.length === 0) return 0
  if (confidence <= stops[0].confidence) return stops[0].density
  const last = stops[stops.length - 1]
  if (confidence >= last.confidence) return last.density
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (confidence <= stops[i + 1].confidence) {
      const span = stops[i + 1].confidence - stops[i].confidence
      const t = span > 0 ? (confidence - stops[i].confidence) / span : 0
      return stops[i].density + t * (stops[i + 1].density - stops[i].density)
    }
  }
  return last.density
}

export type BackgroundPatternSettings = {
  enabled: boolean
  color: string
  opacity: number
  noiseSource: NoiseSource
  ditherPattern: DitherPattern
  // Shape of each dot when ditherPattern is 'dots'. Round by default.
  dotShape: DotShape
  baseFrequency: number
  numOctaves: number
  seed: number
  noiseAmplitude: number
  noiseOffset: number
  patternScale: number // cell size in px for cell-based dither
  ditherPatternOffsetX: number
  // Skip globe-overlapping dot tiles entirely instead of clipping.
  globeDotExclusionEnabled: boolean
  globeDotExclusionPaddingPx: number
  // Radius-scale capability-line dots to separate line from field.
  capabilityLineDotExclusionEnabled: boolean
  capabilityLineDotExclusionPaddingPx: number
  capabilityLineDotRadiusMultiplier: number
  capabilityLineSmoothing: number
  gradientStrength: number
  gradientCurve: number
  gradientAngle: number
  // < 1 produces halftoning even where gradient was fully saturated.
  ditherDensity: number
  // Floor on gradient luminance per strip; independent of gradientStrength.
  minDitherDensity: number
  maxDitherDensity: number
  // >= 2 quantizes the gradient into discrete bands; 0/1 disables.
  densityBands: number
  // Geometric ratio between consecutive band thicknesses; 1 = uniform.
  bandThicknessMultiplier: number
  // Wiggles band borders; 1 = shift up to ±half a band. No effect if densityBands<2.
  bandBoundaryNoise: number
  bandBoundaryNoiseType: BandBoundaryNoiseType
  // Per-axis freq (cycles/px); decoupled x/y for anisotropic streams.
  bandBoundaryNoiseFrequencyX: number
  bandBoundaryNoiseFrequencyY: number
  bandBoundaryNoiseOctaves: number
  // >= 2 quantizes band-border noise into levels; 0/1 = smooth.
  bandBoundaryNoiseQuantizeSteps: number
  // With densityBands>=2, render only the band under the cursor.
  densityBandsHoverOnly: boolean
  // Hover-highlight edge feather as a fraction of band thickness.
  bandHighlightEdgeSoftness: number
  // Min dot-radius gap (px) so low bands don't collapse to equal dots.
  minDotRadiusStepPx: number
  // Hovered-band dot mult; combined with additivePx via max(base*mult, base+add).
  bandHighlightDotRadiusMultiplier: number
  // Min additive dot bump (px) on hover; 'dots' only.
  bandHighlightDotRadiusAdditivePx: number
  // HSL lightness mult on hovered-band overlay color. Range 0..2.
  bandHighlightBrightnessMultiplier: number
  // HSL saturation mult on hovered-band overlay color. Range 0..2.
  bandHighlightSaturationMultiplier: number
  lossOfControlLineYOffset: number
  lossOfControlGradientStripOpacity: number
  // When true, the top-anchored loss-of-control dither gradient uses the chart
  // top (y=0) as its max-density anchor instead of the upper loss-of-control
  // line. This decouples the dither from the modeled upper risk bound so the
  // dots taper continuously from the very top of the chart down to the curve,
  // with no solid max-density plateau above the upper risk line.
  ditherFillToTop: boolean
  // When true, shows the bottom-anchored "trailing actor risk" field — a second
  // dithered gradient (black dots) that rises up from the bottom of the chart,
  // plus its "TRAILING ACTOR RISK" axis label. Driven by the optional
  // `tooSlowRiskByPlan` data on the graph data file.
  showTrailingActorRisk: boolean
  // Color of the "TRAILING ACTOR RISK" axis label (its arrow + vertical text).
  trailingActorRiskColor: string
  // --- Trailing actor risk field: confidence → dither-density map ---
  // The black bottom-anchored field's shade is looked up from this piecewise-
  // linear map: at each tile, confidence = P(the trailing actor reached ≥ that
  // capability) from the CDF, mapped to a dither density (0 = blank, 1 = solid)
  // by linear interpolation between these stops. Editable in the ⌘+G "Trailing
  // actor risk" section. Kept sorted by ascending confidence; ≥ 2 stops.
  trailingActorRiskConfidenceStops: TrailingActorRiskConfidenceStop[]
  // --- Loss-of-control (red) field: risk → dither-density map ---
  // Same piecewise-linear model for the red field. Its "confidence" is the risk
  // position: 0 at the loss-of-control lower line (risk onset), 1 at the upper
  // line (risk certain) — the geometric progress the red gradient already uses,
  // since there's no tabulated takeover CDF. Editable in the ⌘+G "Loss of
  // control (red)" section; defaults reproduce the current continuous gradient.
  redRiskConfidenceStops: ConfidenceDensityStop[]
  // Manual override for which deal-survival "version" of the field is shown, as
  // the conditioning story year (maps to survival = year − deal-start). null
  // follows the live story date so the field recedes as the scenario advances.
  trailingActorRiskConditioningYearOverride: number | null
  // Explicit band-boundary positions; valid set overrides the geometric series.
  bandLowerProgressOverrides?: number[]
  // 0..1; > 0 blends dot radii across boundaries instead of snapping.
  bandDotRadiusBlendAmount?: number

  // --- Capability line colors ---
  // Color of the solid (historical) capability-line segment.
  capabilityLineSolidColor: string
  // Color of the dashed (extrapolated) capability-line segment + its markers.
  capabilityLineDashedColor: string

  // --- Capability line geometry ---
  // Stroke width (px) of the solid (past) capability-line segment.
  capabilityLineStrokeWidth: number
  // Whether the future (extrapolated) segment renders as a dashed line. When
  // false it draws solid, ignoring `capabilityFutureLineDashLength`.
  capabilityFutureLineDashed: boolean
  // Stroke width (px) of the future (extrapolated) capability-line segment.
  capabilityFutureLineStrokeWidth: number
  // Dash length (px) on the future capability-line segment when dashed. The
  // gap between dashes is the fixed `CAPABILITIES_FUTURE_LINE_DASH_GAP`.
  capabilityFutureLineDashLength: number
  // Font size (px) of the "AI CAPABILITIES" line label.
  aiCapabilitiesLabelFontSize: number

  // --- Capability risk split ---
  // When true, the graph background dither is split by the capability line:
  // dots on the lower-right (already-past-the-line, "at risk") side keep the
  // overlay `color`, while dots on the upper-left (safe) side render in
  // `capabilitySafeSideColor`.
  capabilityRiskSplitEnabled: boolean
  // Dither color for the safe (upper-left) side when the split is enabled.
  capabilitySafeSideColor: string
  // Per-side density for the safe (upper-left) pass when the split is enabled,
  // so the safe side can read sparser/denser than the risk side independently.
  // These mirror `ditherDensity` / `minDitherDensity` / `maxDitherDensity` but
  // apply only to the safe-side draw.
  safeSideDitherDensity: number
  safeSideMinDitherDensity: number
  safeSideMaxDitherDensity: number
}

// The locked-in design (formerly the "v28" saved version, baked in as the
// unconditional baseline). savedBackgroundPatternSettings.json — the file the
// Cmd+G panel saves to and the server loads at render time — holds the same
// values; this const is the fallback (file missing/unreadable), the sanitizer's
// per-field fill-in, and the panel's "Reset to defaults" target.
export const DEFAULT_BACKGROUND_PATTERN_SETTINGS: BackgroundPatternSettings = {
  enabled: true,
  color: '#992424',
  opacity: 1,
  noiseSource: 'perlinNoise',
  ditherPattern: 'dots',
  dotShape: 'circle',
  baseFrequency: 0.01,
  numOctaves: 1,
  seed: 10,
  noiseAmplitude: 0,
  noiseOffset: 0,
  patternScale: 2,
  ditherPatternOffsetX: -21.2,
  globeDotExclusionEnabled: false,
  globeDotExclusionPaddingPx: 0,
  capabilityLineDotExclusionEnabled: true,
  capabilityLineDotExclusionPaddingPx: 4.5,
  capabilityLineDotRadiusMultiplier: 0,
  capabilityLineSmoothing: 1,
  gradientStrength: 1,
  gradientCurve: 0.9,
  gradientAngle: 0,
  ditherDensity: 0.52,
  minDitherDensity: 0,
  maxDitherDensity: 0.59,
  densityBands: 0,
  bandThicknessMultiplier: 1.65,
  bandBoundaryNoise: 0,
  bandBoundaryNoiseType: 'value',
  bandBoundaryNoiseFrequencyX: 0.166,
  bandBoundaryNoiseFrequencyY: 0.3,
  bandBoundaryNoiseOctaves: 6,
  bandBoundaryNoiseQuantizeSteps: 0,
  densityBandsHoverOnly: false,
  bandHighlightEdgeSoftness: 0,
  minDotRadiusStepPx: 0.1,
  bandHighlightDotRadiusMultiplier: 0,
  bandHighlightDotRadiusAdditivePx: 0,
  bandHighlightBrightnessMultiplier: 1.01,
  bandHighlightSaturationMultiplier: 0.36,
  lossOfControlLineYOffset: -80,
  lossOfControlGradientStripOpacity: 0,
  ditherFillToTop: true,
  showTrailingActorRisk: true,
  trailingActorRiskColor: '#000000',
  // Confidence → dither-density stops (blank at zero confidence, rising to
  // dense as confidence climbs). Confidence tops out ~0.65 in the data, so the
  // last stop's density is what the most-confident floor tiles read at.
  trailingActorRiskConfidenceStops: [
    { confidence: 0, density: 0 },
    { confidence: 0.1, density: 0.06 },
    { confidence: 0.3, density: 0.28 },
    { confidence: 0.65, density: 0.8 },
  ],
  // Red risk → density. Defaults trace the previous continuous gradient
  // (density ≈ ditherDensity·progress^gradientCurve = 0.52·progress^0.9) so the
  // red field is visually unchanged until these stops are edited.
  redRiskConfidenceStops: [
    { confidence: 0, density: 0 },
    { confidence: 0.25, density: 0.15 },
    { confidence: 0.5, density: 0.28 },
    { confidence: 0.75, density: 0.4 },
    { confidence: 1, density: 0.52 },
  ],
  trailingActorRiskConditioningYearOverride: null,
  capabilityLineSolidColor: '#000000',
  capabilityLineDashedColor: '#000000',
  capabilityLineStrokeWidth: 2.5,
  capabilityFutureLineDashed: true,
  capabilityFutureLineStrokeWidth: 2,
  capabilityFutureLineDashLength: 4,
  aiCapabilitiesLabelFontSize: 12,
  capabilityRiskSplitEnabled: false,
  capabilitySafeSideColor: '#000000',
  safeSideDitherDensity: 0,
  safeSideMinDitherDensity: 0,
  safeSideMaxDitherDensity: 0,
}

const inferNoiseSource = (raw: Record<string, unknown>): NoiseSource => {
  if (
    typeof raw.noiseSource === 'string'
    && NOISE_SOURCES.includes(raw.noiseSource as NoiseSource)
  ) {
    return raw.noiseSource as NoiseSource
  }
  // v2 had a unified patternType combining noise + pattern.
  if (raw.patternType === 'perlinNoise') return 'perlinNoise'
  if (raw.patternType === 'turbulence') return 'turbulence'
  if (typeof raw.patternType === 'string') return 'none'
  // v1 had only noiseType.
  if (raw.noiseType === 'turbulence') return 'turbulence'
  if (raw.noiseType === 'fractalNoise') return 'perlinNoise'
  return DEFAULT_BACKGROUND_PATTERN_SETTINGS.noiseSource
}

const inferDitherPattern = (raw: Record<string, unknown>): DitherPattern => {
  if (
    typeof raw.ditherPattern === 'string'
    && DITHER_PATTERNS.includes(raw.ditherPattern as DitherPattern)
  ) {
    return raw.ditherPattern as DitherPattern
  }
  // v2 used patternType for both noise + pattern.
  switch (raw.patternType) {
    case 'checkerboard':
    case 'bayer4':
    case 'bayer8':
    case 'horizontalLines':
    case 'verticalLines':
    case 'diagonalLines':
    case 'dots':
      return raw.patternType as DitherPattern
    default:
      return DEFAULT_BACKGROUND_PATTERN_SETTINGS.ditherPattern
  }
}

// Validate the confidence→density stops: coerce each value to [0,1] and fall
// back to the default when fewer than two usable stops survive. Order is left
// as authored (this runs on every edit, so re-sorting would jump the editor's
// rows mid-keystroke); consumers sort a copy where interpolation needs it.
const sanitizeConfidenceStops = (
  raw: unknown,
  fallback: ConfidenceDensityStop[],
): ConfidenceDensityStop[] => {
  if (!Array.isArray(raw)) return fallback
  const clamp01 = (v: number) => Math.min(1, Math.max(0, v))
  const cleaned = raw
    .filter((s): s is { confidence: number; density: number } =>
      !!s && typeof s === 'object'
      && Number.isFinite((s as { confidence?: unknown }).confidence)
      && Number.isFinite((s as { density?: unknown }).density))
    .map((s) => ({ confidence: clamp01(s.confidence), density: clamp01(s.density) }))
  return cleaned.length >= 2 ? cleaned : fallback
}

export const sanitizeBackgroundPatternSettings = (
  raw: unknown
): BackgroundPatternSettings => {
  if (!raw || typeof raw !== 'object') return DEFAULT_BACKGROUND_PATTERN_SETTINGS
  const candidate = raw as Record<string, unknown>
  return {
    enabled: typeof candidate.enabled === 'boolean'
      ? candidate.enabled
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.enabled,
    color: typeof candidate.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(candidate.color as string)
      ? candidate.color as string
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.color,
    opacity: Number.isFinite(candidate.opacity)
      ? Math.min(1, Math.max(0, candidate.opacity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.opacity,
    noiseSource: inferNoiseSource(candidate),
    ditherPattern: inferDitherPattern(candidate),
    baseFrequency: Number.isFinite(candidate.baseFrequency)
      ? Math.min(4, Math.max(0.001, candidate.baseFrequency as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.baseFrequency,
    numOctaves: Number.isFinite(candidate.numOctaves)
      ? Math.min(8, Math.max(1, Math.round(candidate.numOctaves as number)))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.numOctaves,
    seed: Number.isFinite(candidate.seed)
      ? Math.round(candidate.seed as number)
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.seed,
    noiseAmplitude: Number.isFinite(candidate.noiseAmplitude)
      ? Math.min(4, Math.max(0, candidate.noiseAmplitude as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.noiseAmplitude,
    noiseOffset: Number.isFinite(candidate.noiseOffset)
      ? Math.min(1, Math.max(-1, candidate.noiseOffset as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.noiseOffset,
    patternScale: Number.isFinite(candidate.patternScale)
      ? Math.min(64, Math.max(1, candidate.patternScale as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.patternScale,
    dotShape: DOT_SHAPES.includes(candidate.dotShape as DotShape)
      ? (candidate.dotShape as DotShape)
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.dotShape,
    ditherPatternOffsetX: Number.isFinite(candidate.ditherPatternOffsetX)
      ? Math.min(64, Math.max(-64, candidate.ditherPatternOffsetX as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.ditherPatternOffsetX,
    globeDotExclusionEnabled: typeof candidate.globeDotExclusionEnabled === 'boolean'
      ? candidate.globeDotExclusionEnabled
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.globeDotExclusionEnabled,
    globeDotExclusionPaddingPx: Number.isFinite(candidate.globeDotExclusionPaddingPx)
      ? Math.min(32, Math.max(0, candidate.globeDotExclusionPaddingPx as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.globeDotExclusionPaddingPx,
    capabilityLineDotExclusionEnabled:
      typeof candidate.capabilityLineDotExclusionEnabled === 'boolean'
        ? candidate.capabilityLineDotExclusionEnabled
        : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineDotExclusionEnabled,
    capabilityLineDotExclusionPaddingPx: Number.isFinite(
      candidate.capabilityLineDotExclusionPaddingPx
    )
      ? Math.min(32, Math.max(0, candidate.capabilityLineDotExclusionPaddingPx as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineDotExclusionPaddingPx,
    capabilityLineDotRadiusMultiplier: Number.isFinite(
      candidate.capabilityLineDotRadiusMultiplier
    )
      ? Math.min(1, Math.max(0, candidate.capabilityLineDotRadiusMultiplier as number))
      : Number.isFinite(candidate.capabilityLineDotOverlapOpacity)
        ? Math.min(1, Math.max(0, candidate.capabilityLineDotOverlapOpacity as number))
        : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineDotRadiusMultiplier,
    capabilityLineSmoothing: Number.isFinite(candidate.capabilityLineSmoothing)
      ? Math.min(1, Math.max(0, candidate.capabilityLineSmoothing as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineSmoothing,
    gradientStrength: Number.isFinite(candidate.gradientStrength)
      ? Math.min(1, Math.max(0, candidate.gradientStrength as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.gradientStrength,
    gradientCurve: Number.isFinite(candidate.gradientCurve)
      ? Math.min(4, Math.max(0.2, candidate.gradientCurve as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.gradientCurve,
    gradientAngle: Number.isFinite(candidate.gradientAngle)
      ? Math.min(90, Math.max(0, candidate.gradientAngle as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.gradientAngle,
    ditherDensity: Number.isFinite(candidate.ditherDensity)
      ? Math.min(2, Math.max(0, candidate.ditherDensity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.ditherDensity,
    minDitherDensity: Number.isFinite(candidate.minDitherDensity)
      ? Math.min(1, Math.max(0, candidate.minDitherDensity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.minDitherDensity,
    maxDitherDensity: Number.isFinite(candidate.maxDitherDensity)
      ? Math.min(1, Math.max(0, candidate.maxDitherDensity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.maxDitherDensity,
    densityBands: Number.isFinite(candidate.densityBands)
      ? Math.min(32, Math.max(0, Math.round(candidate.densityBands as number)))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.densityBands,
    bandThicknessMultiplier: Number.isFinite(candidate.bandThicknessMultiplier)
      ? Math.min(4, Math.max(0.25, candidate.bandThicknessMultiplier as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandThicknessMultiplier,
    bandBoundaryNoise: Number.isFinite(candidate.bandBoundaryNoise)
      ? Math.min(1, Math.max(0, candidate.bandBoundaryNoise as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandBoundaryNoise,
    bandBoundaryNoiseType: typeof candidate.bandBoundaryNoiseType === 'string'
      && BAND_BOUNDARY_NOISE_TYPES.includes(
        candidate.bandBoundaryNoiseType as BandBoundaryNoiseType
      )
      ? candidate.bandBoundaryNoiseType as BandBoundaryNoiseType
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandBoundaryNoiseType,
    bandBoundaryNoiseFrequencyX: Number.isFinite(candidate.bandBoundaryNoiseFrequencyX)
      ? Math.min(4, Math.max(0.001, candidate.bandBoundaryNoiseFrequencyX as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandBoundaryNoiseFrequencyX,
    bandBoundaryNoiseFrequencyY: Number.isFinite(candidate.bandBoundaryNoiseFrequencyY)
      ? Math.min(4, Math.max(0.001, candidate.bandBoundaryNoiseFrequencyY as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandBoundaryNoiseFrequencyY,
    bandBoundaryNoiseOctaves: Number.isFinite(candidate.bandBoundaryNoiseOctaves)
      ? Math.min(8, Math.max(1, Math.round(candidate.bandBoundaryNoiseOctaves as number)))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandBoundaryNoiseOctaves,
    bandBoundaryNoiseQuantizeSteps: Number.isFinite(candidate.bandBoundaryNoiseQuantizeSteps)
      ? Math.min(16, Math.max(0, Math.round(candidate.bandBoundaryNoiseQuantizeSteps as number)))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandBoundaryNoiseQuantizeSteps,
    densityBandsHoverOnly: typeof candidate.densityBandsHoverOnly === 'boolean'
      ? candidate.densityBandsHoverOnly
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.densityBandsHoverOnly,
    bandHighlightEdgeSoftness: Number.isFinite(candidate.bandHighlightEdgeSoftness)
      ? Math.min(1, Math.max(0, candidate.bandHighlightEdgeSoftness as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandHighlightEdgeSoftness,
    minDotRadiusStepPx: Number.isFinite(candidate.minDotRadiusStepPx)
      ? Math.min(4, Math.max(0, candidate.minDotRadiusStepPx as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.minDotRadiusStepPx,
    bandHighlightDotRadiusMultiplier: Number.isFinite(candidate.bandHighlightDotRadiusMultiplier)
      ? Math.min(4, Math.max(0, candidate.bandHighlightDotRadiusMultiplier as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandHighlightDotRadiusMultiplier,
    bandHighlightDotRadiusAdditivePx: Number.isFinite(candidate.bandHighlightDotRadiusAdditivePx)
      ? Math.min(8, Math.max(0, candidate.bandHighlightDotRadiusAdditivePx as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandHighlightDotRadiusAdditivePx,
    bandHighlightBrightnessMultiplier: Number.isFinite(candidate.bandHighlightBrightnessMultiplier)
      ? Math.min(2, Math.max(0, candidate.bandHighlightBrightnessMultiplier as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandHighlightBrightnessMultiplier,
    bandHighlightSaturationMultiplier: Number.isFinite(candidate.bandHighlightSaturationMultiplier)
      ? Math.min(2, Math.max(0, candidate.bandHighlightSaturationMultiplier as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.bandHighlightSaturationMultiplier,
    lossOfControlLineYOffset: Number.isFinite(candidate.lossOfControlLineYOffset)
      ? Math.min(80, Math.max(-80, candidate.lossOfControlLineYOffset as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.lossOfControlLineYOffset,
    lossOfControlGradientStripOpacity: Number.isFinite(
      candidate.lossOfControlGradientStripOpacity
    )
      ? Math.min(1, Math.max(0, candidate.lossOfControlGradientStripOpacity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.lossOfControlGradientStripOpacity,
    ditherFillToTop: typeof candidate.ditherFillToTop === 'boolean'
      ? candidate.ditherFillToTop
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.ditherFillToTop,
    showTrailingActorRisk: typeof candidate.showTrailingActorRisk === 'boolean'
      ? candidate.showTrailingActorRisk
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.showTrailingActorRisk,
    trailingActorRiskColor: isHexColor(candidate.trailingActorRiskColor)
      ? candidate.trailingActorRiskColor
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.trailingActorRiskColor,
    trailingActorRiskConfidenceStops: sanitizeConfidenceStops(
      candidate.trailingActorRiskConfidenceStops,
      DEFAULT_BACKGROUND_PATTERN_SETTINGS.trailingActorRiskConfidenceStops,
    ),
    redRiskConfidenceStops: sanitizeConfidenceStops(
      candidate.redRiskConfidenceStops,
      DEFAULT_BACKGROUND_PATTERN_SETTINGS.redRiskConfidenceStops,
    ),
    trailingActorRiskConditioningYearOverride:
      Number.isFinite(candidate.trailingActorRiskConditioningYearOverride)
        ? Math.round(candidate.trailingActorRiskConditioningYearOverride as number)
        : DEFAULT_BACKGROUND_PATTERN_SETTINGS.trailingActorRiskConditioningYearOverride,
    bandLowerProgressOverrides: sanitizeBandLowerProgressOverrides(
      candidate.bandLowerProgressOverrides,
    ),
    bandDotRadiusBlendAmount: Number.isFinite(candidate.bandDotRadiusBlendAmount)
      ? Math.min(1, Math.max(0, candidate.bandDotRadiusBlendAmount as number))
      : undefined,
    capabilityLineSolidColor: isHexColor(candidate.capabilityLineSolidColor)
      ? candidate.capabilityLineSolidColor
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineSolidColor,
    capabilityLineDashedColor: isHexColor(candidate.capabilityLineDashedColor)
      ? candidate.capabilityLineDashedColor
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineDashedColor,
    capabilityLineStrokeWidth: Number.isFinite(candidate.capabilityLineStrokeWidth)
      ? Math.min(20, Math.max(0, candidate.capabilityLineStrokeWidth as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityLineStrokeWidth,
    capabilityFutureLineDashed: typeof candidate.capabilityFutureLineDashed === 'boolean'
      ? candidate.capabilityFutureLineDashed
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityFutureLineDashed,
    capabilityFutureLineStrokeWidth: Number.isFinite(candidate.capabilityFutureLineStrokeWidth)
      ? Math.min(20, Math.max(0, candidate.capabilityFutureLineStrokeWidth as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityFutureLineStrokeWidth,
    capabilityFutureLineDashLength: Number.isFinite(candidate.capabilityFutureLineDashLength)
      ? Math.min(40, Math.max(0.5, candidate.capabilityFutureLineDashLength as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityFutureLineDashLength,
    aiCapabilitiesLabelFontSize: Number.isFinite(candidate.aiCapabilitiesLabelFontSize)
      ? Math.min(48, Math.max(4, candidate.aiCapabilitiesLabelFontSize as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.aiCapabilitiesLabelFontSize,
    capabilityRiskSplitEnabled: typeof candidate.capabilityRiskSplitEnabled === 'boolean'
      ? candidate.capabilityRiskSplitEnabled
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilityRiskSplitEnabled,
    capabilitySafeSideColor: isHexColor(candidate.capabilitySafeSideColor)
      ? candidate.capabilitySafeSideColor
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.capabilitySafeSideColor,
    safeSideDitherDensity: Number.isFinite(candidate.safeSideDitherDensity)
      ? Math.min(2, Math.max(0, candidate.safeSideDitherDensity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.safeSideDitherDensity,
    safeSideMinDitherDensity: Number.isFinite(candidate.safeSideMinDitherDensity)
      ? Math.min(1, Math.max(0, candidate.safeSideMinDitherDensity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.safeSideMinDitherDensity,
    safeSideMaxDitherDensity: Number.isFinite(candidate.safeSideMaxDitherDensity)
      ? Math.min(1, Math.max(0, candidate.safeSideMaxDitherDensity as number))
      : DEFAULT_BACKGROUND_PATTERN_SETTINGS.safeSideMaxDitherDensity,
  }
}

// Valid set: length>=2, finite in [0,1], non-decreasing, first=0, last=1.
const sanitizeBandLowerProgressOverrides = (
  raw: unknown,
): number[] | undefined => {
  if (!Array.isArray(raw)) return undefined
  if (raw.length < 2) return undefined
  const arr: number[] = []
  for (const v of raw) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return undefined
    if (v < 0 || v > 1) return undefined
    arr.push(v)
  }
  if (arr[0] !== 0) return undefined
  if (arr[arr.length - 1] !== 1) return undefined
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] < arr[i - 1]) return undefined
  }
  return arr
}
